import {
  useCallback, useState,
} from 'react';
import { useLatest } from '~/_shared/utils/hooks/useLatest';
import { useUserSign } from '~/authentication/loginPage/loginForm/useUserSign';
import { useTranslation } from '../../_shared/utils/hooks';
import {
  getEmailVerificationRequest, resendEmailVerificationRequest,
} from './verifyEmail.repository';

export const useVerifyEmail = () => {
  const [t] = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const { onLoginSuccess } = useUserSign();
  // onLoginSuccess is recreated on every render, keep the verification callback stable
  const onLoginSuccessRef = useLatest(onLoginSuccess);

  const getEmailVerification = useCallback((props: {
    expires: string;
    hash: string;
    id: string;
    userEmail: string;
    signature: string;
  }) => {
    setIsLoading(true);
    getEmailVerificationRequest(props.expires, props.hash, props.id, props.userEmail, props.signature)
      .then((response) => {
        setIsLoading(false);
        setIsVerified(true);
        // The verification also signs the user in on the BE, so start the auth on the FE as well.
        // When the sign in part failed, the user data request fails too and the user is offered the login page instead.
        onLoginSuccessRef.current(response);
      }).catch((error) => {
        setIsLoading(false);
        setIsVerified(false);
        if (error.responseStatus === 403) {
          setErrorMessage(t('This functionality is not available for current user.'));
        }
        else {
          setErrorMessage(t('The provided email address could not be verified'));
        }
      });
  }, [onLoginSuccessRef, t]);

  const resendEmailVerification = useCallback((userEmail: string, successCallback: () => void, failureCallback: () => void) => {
    setIsEmailSending(true);
    resendEmailVerificationRequest(userEmail)
      .then(() => {
        setIsEmailSending(false);
        successCallback();
      })
      .catch(() => {
        setIsEmailSending(false);
        setErrorMessage(t('We were unable to re-send the verification email, please try again. If this problem persists please contact our customer support.'));
        failureCallback();
      });

  }, [t]);

  return {
    isLoading,
    isEmailSending,
    isVerified,
    errorMessage,
    getEmailVerification,
    resendEmailVerification,
  };
};
